(function createTitanScanSession() {
  'use strict';

  function getDeviceSignals(browserWindow = window, browserNavigator = navigator) {
    return Object.freeze({
      coarsePointer: browserWindow.matchMedia('(pointer: coarse)').matches,
      maxTouchPoints: browserNavigator.maxTouchPoints || 0,
      viewportWidth: browserWindow.innerWidth,
      userAgent: browserNavigator.userAgent || '',
    });
  }

  function emptyMetrics(mode) {
    return Object.freeze({
      mode,
      hits: 0,
      misses: 0,
      accuracy: 0,
      inputScore: 0,
      averageInterval: null,
      fastestInterval: null,
      reactions: [],
    });
  }

  function freezeState(nextState) {
    return Object.freeze({
      ...nextState,
      answers: Object.freeze({ ...nextState.answers }),
      device: Object.freeze({ ...nextState.device }),
      metrics: Object.freeze({
        ...nextState.metrics,
        reactions: Object.freeze([...(nextState.metrics.reactions || [])]),
      }),
      completed: Object.freeze({ ...nextState.completed }),
      preferences: Object.freeze({ ...nextState.preferences }),
    });
  }

  function createInitialState(data, logic, signals = getDeviceSignals()) {
    const mode = logic.detectInputMode(signals);
    return freezeState({
      version: data.schemaVersion,
      screen: 'intro',
      answers: {
        game: null,
        style: null,
        monitorHz: null,
        budget: 180000,
        budgetUnlimited: false,
        priority: null,
        streaming: null,
      },
      device: { mode, override: null, signals },
      metrics: emptyMetrics(mode),
      recommendedProductId: null,
      completed: { input: false, reaction: false },
      preferences: { muted: true },
    });
  }

  function persistableScreen(currentState) {
    if (currentState.screen === 'input-test') return 'input-intro';
    if (currentState.screen === 'reaction-test') return 'reaction-intro';
    if (currentState.screen === 'analyzing') {
      return currentState.metrics.reactions.length === 3 ? 'result' : 'reaction-intro';
    }
    return currentState.screen;
  }

  function storageSnapshot(currentState) {
    return {
      version: currentState.version,
      screen: persistableScreen(currentState),
      answers: currentState.answers,
      device: {
        mode: currentState.device.mode,
        override: currentState.device.override,
      },
      metrics: currentState.metrics,
      recommendedProductId: currentState.recommendedProductId,
      completed: currentState.completed,
      preferences: currentState.preferences,
    };
  }

  function normalizeResumeScreen(screen, metrics) {
    if (screen === 'input-test') return 'input-intro';
    if (screen === 'reaction-test') return 'reaction-intro';
    if (screen === 'analyzing') {
      return metrics.reactions.length === 3 ? 'result' : 'reaction-intro';
    }
    return screen;
  }

  function restoreStoredState(raw, initialState, logic, data) {
    const validated = logic.validatePersistedState(JSON.parse(raw), data);
    if (!validated) return null;
    const screen = normalizeResumeScreen(validated.screen, validated.metrics);
    return freezeState({
      ...initialState,
      ...validated,
      screen,
      device: {
        ...validated.device,
        signals: initialState.device.signals,
      },
      completed: {
        input: validated.completed.input || ['reaction-intro', 'result'].includes(screen),
        reaction: validated.completed.reaction || screen === 'result',
      },
    });
  }

  function stateFromHash(hash, initialState, logic, data) {
    const restored = logic.decodeResultHash(hash, data);
    if (!restored) return null;
    try {
      logic.buildDiagnosis(restored, data);
      return freezeState({
        ...initialState,
        screen: 'result',
        answers: restored.answers,
        device: {
          mode: restored.device.mode,
          override: restored.device.mode,
          signals: initialState.device.signals,
        },
        metrics: restored.metrics,
        recommendedProductId: restored.recommendedProductId,
        completed: { input: true, reaction: true },
      });
    } catch (error) {
      return null;
    }
  }

  window.TitanScanSession = Object.freeze({
    createInitialState,
    emptyMetrics,
    freezeState,
    getDeviceSignals,
    normalizeResumeScreen,
    persistableScreen,
    restoreStoredState,
    stateFromHash,
    storageSnapshot,
  });
})();
